import React, {
    FormEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import apiRequest, { streamChatRequest } from './api';
import { clearMidwaySession } from './auth/midway';
import {
    ChatArtifact,
    ChatMessage,
    ChatResponse,
    Citation,
    ToolActivity,
} from './chat-types';
import RichMessage from './components/RichMessage';
import './App.css';

type RuntimeConfig = {
    region: string;
    user: {
        alias: string;
        fullName?: string;
        photoUrl?: string;
        authStatus?: 'verified' | 'expired' | 'unavailable';
    };
};

type ComposerProps = {
    draft: string;
    disabled: boolean;
    variant: 'hero' | 'dock';
    onChange: (value: string) => void;
    onSubmit: (event: FormEvent) => void;
    onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
};

const PROMPT_SUGGESTIONS = [
    {
        category: 'Sites',
        title: 'Explore onboarded sites',
        prompt: 'Which AR sites are onboarded, and how many have open action plans?',
        icon: 'sites',
    },
    {
        category: 'Metrics',
        title: 'Understand a target',
        prompt: "What is the ETI Productive Time target for DTW1, and how is that metric defined?",
        icon: 'metric',
    },
    {
        category: 'Opportunities',
        title: 'Summarize open work',
        prompt: 'Summarize the open opportunity profiles for DTW1 and their action plan comments.',
        icon: 'opportunity',
    },
    {
        category: 'Network',
        title: 'Compare performance',
        prompt: 'Compare open action plans across AR sites and highlight the largest opportunities.',
        icon: 'network',
    },
    {
        category: 'Anomalies',
        title: 'Spot unusual movement',
        prompt: 'Which AR sites have unusual week-over-week metric movements, and what changed?',
        icon: 'anomaly',
    },
    {
        category: 'Business reviews',
        title: 'Draft a weekly narrative',
        prompt: 'Draft a concise weekly business review narrative for DTW1 using current metrics and action plans.',
        icon: 'narrative',
    },
];

function createSessionId(): string {
    return crypto.randomUUID();
}

function PromptIcon({ name }: { name: string }) {
    if (name === 'metric') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 19V9m7 10V5m7 14v-7" />
                <path d="M3 19h18" />
            </svg>
        );
    }
    if (name === 'opportunity') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 18h6M10 22h4" />
                <path d="M8.4 14.5A7 7 0 1 1 15.6 14.5c-.9.7-1.4 1.4-1.6 2.5h-4c-.2-1.1-.7-1.8-1.6-2.5Z" />
            </svg>
        );
    }
    if (name === 'network') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="5" cy="12" r="2.5" />
                <circle cx="19" cy="6" r="2.5" />
                <circle cx="19" cy="18" r="2.5" />
                <path d="m7.4 11.1 9.2-4.2M7.4 12.9l9.2 4.2" />
            </svg>
        );
    }
    if (name === 'anomaly') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 19h16" />
                <path d="m5 15 4-4 3 2 6-7" />
                <circle cx="18" cy="6" r="2" />
            </svg>
        );
    }
    if (name === 'narrative') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 3h8l4 4v14H6z" />
                <path d="M14 3v5h5M9 12h6M9 16h6" />
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 20V8l8-4 8 4v12" />
            <path d="M8 20v-7h8v7M3 20h18" />
        </svg>
    );
}

function BrandMark() {
    return (
        <span className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
        </span>
    );
}

function SendIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m5 12 14-7-4 14-3-5-7-2Z" />
            <path d="m12 14 7-9" />
        </svg>
    );
}

function IdentityStatus({
    status,
}: {
    status: NonNullable<RuntimeConfig['user']['authStatus']>;
}) {
    const labels = {
        verified: 'Midway verified',
        expired: 'Midway session expired',
        unavailable: 'Midway status unavailable',
    };

    return (
        <span
            className={`identity-status ${status}`}
            role="img"
            aria-label={labels[status]}
            title={labels[status]}
        >
            {status === 'verified' ? (
                <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="m4.2 8.1 2.3 2.3 5.3-5.2" />
                </svg>
            ) : (
                <span aria-hidden="true">{status === 'expired' ? '!' : '?'}</span>
            )}
        </span>
    );
}

function IdentityProfile({ user }: { user: RuntimeConfig['user'] }) {
    const [photoFailed, setPhotoFailed] = useState(false);
    const resolvedName = user.fullName?.trim();
    const hasDistinctName =
        Boolean(resolvedName) && resolvedName?.toLowerCase() !== user.alias.toLowerCase();
    const displayName = hasDistinctName ? resolvedName : user.alias;
    const photoUrl =
        user.photoUrl ??
        `https://badgephotos.corp.amazon.com/?uid=${encodeURIComponent(user.alias)}`;
    const authStatus = user.authStatus ?? 'verified';

    return (
        <>
            <span className="identity-avatar">
                {!photoFailed && (
                    <img
                        src={photoUrl}
                        alt={`${displayName} profile`}
                        onError={() => setPhotoFailed(true)}
                    />
                )}
                {photoFailed && (
                    <span className="identity-avatar-fallback" aria-hidden="true">
                        {user.alias.slice(0, 1).toUpperCase()}
                    </span>
                )}
            </span>
            <span className="identity-details">
                <strong>{displayName}</strong>
                <span className="identity-alias">
                    @{user.alias}
                    <IdentityStatus status={authStatus} />
                </span>
            </span>
        </>
    );
}

function Composer({
    draft,
    disabled,
    variant,
    onChange,
    onSubmit,
    onKeyDown,
}: ComposerProps) {
    return (
        <form className={`prompt-composer ${variant}`} onSubmit={onSubmit}>
            <div className="composer-glow" />
            <div className="composer-input-row">
                <span className="composer-spark" aria-hidden="true">
                    ✦
                </span>
                <textarea
                    value={draft}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Ask about a site, metric, target, opportunity, or action plan"
                    maxLength={8000}
                    rows={variant === 'hero' ? 2 : 1}
                    disabled={disabled}
                    aria-label="Message"
                />
                <button
                    className="send-button"
                    type="submit"
                    disabled={!draft.trim() || disabled}
                    aria-label="Send message"
                >
                    <SendIcon />
                </button>
            </div>
            <div className="composer-meta">
                <span>RTB data • Read only • Responses include sources</span>
                <span>{draft.length}/8000</span>
            </div>
        </form>
    );
}

function ThinkingIndicator({ message }: { message: string }) {
    return (
        <div className="thinking-card" role="status">
            <span className="thinking-spinner" aria-hidden="true" />
            <div>
                <strong>{message}</strong>
                <span className="thinking-subtitle">Using your verified, read-only access</span>
            </div>
            <span className="thinking-progress" aria-hidden="true" />
        </div>
    );
}

function normalizeCitations(citations: ChatResponse['citations']): Citation[] {
    return (citations ?? []).map((citation) =>
        typeof citation === 'string' ? { label: citation } : citation,
    );
}

function App() {
    const [config, setConfig] = useState<RuntimeConfig | null>(null);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [authError, setAuthError] = useState('');
    const [sessionId, setSessionId] = useState(() => createSessionId());
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [chatError, setChatError] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const hasConversation = messages.length > 0;

    const userQuestions = useMemo(
        () => messages.filter((message) => message.role === 'user').slice(-6).reverse(),
        [messages],
    );

    const connectWithMidway = useCallback(async () => {
        setAuthError('');
        setIsAuthenticating(true);
        try {
            const runtimeConfig = await apiRequest<RuntimeConfig>('/api/config');
            setConfig(runtimeConfig);
        } catch (error) {
            setAuthError(
                error instanceof Error ? error.message : 'Unable to authenticate with Midway',
            );
        } finally {
            setIsAuthenticating(false);
        }
    }, []);

    useEffect(() => {
        connectWithMidway();
    }, [connectWithMidway]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
    }, [messages]);

    function endLocalSession() {
        clearMidwaySession();
        setConfig(null);
        setMessages([]);
        setSessionId(createSessionId());
        setChatError('');
    }

    function startNewChat() {
        setSessionId(createSessionId());
        setMessages([]);
        setChatError('');
        setDraft('');
    }

    function appendAssistantText(messageId: string, delta: string) {
        setMessages((current) =>
            current.map((message) =>
                message.id === messageId
                    ? { ...message, text: `${message.text}${delta}`, status: 'streaming' }
                    : message,
            ),
        );
    }

    function updateAssistant(messageId: string, update: Partial<ChatMessage>) {
        setMessages((current) =>
            current.map((message) =>
                message.id === messageId ? { ...message, ...update } : message,
            ),
        );
    }

    function appendArtifact(messageId: string, artifact: ChatArtifact) {
        setMessages((current) =>
            current.map((message) =>
                message.id === messageId
                    ? { ...message, artifacts: [...(message.artifacts ?? []), artifact] }
                    : message,
            ),
        );
    }

    function appendCitation(messageId: string, citation: Citation) {
        setMessages((current) =>
            current.map((message) =>
                message.id === messageId
                    ? { ...message, citations: [...(message.citations ?? []), citation] }
                    : message,
            ),
        );
    }

    function updateTool(messageId: string, activity?: ToolActivity) {
        if (!activity) {
            return;
        }
        setMessages((current) =>
            current.map((message) => {
                if (message.id !== messageId) {
                    return message;
                }
                const tools = [...(message.tools ?? [])];
                const index = tools.findIndex((tool) => tool.name === activity.name);
                if (index >= 0) {
                    tools[index] = activity;
                } else {
                    tools.push(activity);
                }
                return { ...message, tools };
            }),
        );
    }

    async function submitPrompt(rawPrompt: string) {
        const prompt = rawPrompt.trim();
        if (!prompt || !config || isSending) {
            return;
        }

        const userMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'user',
            text: prompt,
            status: 'complete',
        };
        const assistantId = crypto.randomUUID();
        const assistantMessage: ChatMessage = {
            id: assistantId,
            role: 'assistant',
            text: '',
            status: 'streaming',
            progress: 'Connecting to RTB data',
        };

        setMessages((current) => [...current, userMessage, assistantMessage]);
        setDraft('');
        setChatError('');
        setIsSending(true);

        try {
            const result = await streamChatRequest(
                '/api/chat',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'text/event-stream, application/json',
                    },
                    body: JSON.stringify({ message: prompt, sessionId }),
                },
                {
                    onStatus: (message, toolActivity) => {
                        updateAssistant(assistantId, { progress: message });
                        updateTool(assistantId, toolActivity);
                    },
                    onTextDelta: (delta) => appendAssistantText(assistantId, delta),
                    onArtifact: (artifact) => appendArtifact(assistantId, artifact),
                    onCitation: (citation) => appendCitation(assistantId, citation),
                },
            );

            setMessages((current) =>
                current.map((message) =>
                    message.id === assistantId
                        ? {
                              ...message,
                              text:
                                  message.text ||
                                  result.message ||
                                  (result.artifacts?.length
                                      ? ''
                                      : 'The agent returned an empty response.'),
                              status: 'complete',
                              progress: undefined,
                              tools: result.tools ?? result.toolActivity,
                              citations: normalizeCitations(result.citations),
                              artifacts: result.artifacts,
                          }
                        : message,
                ),
            );
        } catch (error) {
            const errorText = error instanceof Error ? error.message : 'Unable to send message';
            setChatError(errorText);
            setMessages((current) =>
                current.map((message) =>
                    message.id === assistantId
                        ? {
                              ...message,
                              status: 'error',
                              text: 'I could not complete that request. Your conversation is still here, so you can try again.',
                              progress: undefined,
                              artifacts: undefined,
                              citations: undefined,
                              tools: undefined,
                          }
                        : message,
                ),
            );
        } finally {
            setIsSending(false);
        }
    }

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            submitPrompt(draft);
        },
        [draft, submitPrompt],
    );

    const handleComposerKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submitPrompt(draft);
            }
        },
        [draft, submitPrompt],
    );

    return (
        <main className={`agent-app ${hasConversation ? 'conversation-mode' : 'home-mode'}`}>
                <div className="ambient-orb orb-one" />
                <div className="ambient-orb orb-two" />
                <header className="agent-header">
                    <button className="brand" type="button" onClick={startNewChat}>
                        <BrandMark />
                        <span>
                            <strong>RTB Assistant</strong>
                            <small>Powered by AgentCore</small>
                        </span>
                    </button>
                    <div className="header-actions">
                        {config && (
                            <>
                                {hasConversation && (
                                    <button
                                        key="header-new-chat"
                                        className="new-chat-button"
                                        type="button"
                                        onClick={startNewChat}
                                    >
                                        <span aria-hidden="true">＋</span>
                                        New chat
                                    </button>
                                )}
                                <div key="identity-profile" className="identity-pill">
                                    <IdentityProfile user={config.user} />
                                    <button type="button" onClick={endLocalSession}>
                                        End
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </header>

                {!config && (
                    <section className="auth-experience" aria-labelledby="auth-heading">
                        <div className="auth-illustration">
                            <BrandMark />
                            <span className="auth-ring ring-one" />
                            <span className="auth-ring ring-two" />
                        </div>
                        <p className="eyebrow">Amazon internal</p>
                        <h1 id="auth-heading">Your RTB workspace is ready</h1>
                        <p>
                            Continue with your Amazon employee identity. There is no separate
                            account, signup, or password to manage.
                        </p>
                        {authError && <p className="error-banner">{authError}</p>}
                        <button
                            className="midway-button"
                            type="button"
                            disabled={isAuthenticating}
                            onClick={connectWithMidway}
                        >
                            {isAuthenticating && <span className="button-spinner" />}
                            {isAuthenticating ? 'Connecting securely…' : 'Continue with Midway'}
                        </button>
                    </section>
                )}
                {config && hasConversation && (
                    <section className="chat-workspace">
                        <aside className="conversation-rail">
                            <button className="rail-new-chat" type="button" onClick={startNewChat}>
                                <span>＋</span>
                                New conversation
                            </button>
                            <div className="rail-section">
                                <span className="rail-label">This session</span>
                                {userQuestions.map((message) => (
                                    <button
                                        key={message.id}
                                        type="button"
                                        onClick={() => setDraft(message.text)}
                                    >
                                        <span aria-hidden="true">⌁</span>
                                        {message.text}
                                    </button>
                                ))}
                            </div>
                            <div className="session-security">
                                <span className="security-icon">✓</span>
                                <div>
                                    <strong>Session only</strong>
                                    <span>Context clears with a new chat</span>
                                </div>
                            </div>
                        </aside>

                        <section className="conversation-panel" aria-label="Conversation">
                            <div className="conversation-heading">
                                <div>
                                    <span className="live-dot" />
                                    <span>RTB Assistant</span>
                                </div>
                                <span>Read-only access</span>
                            </div>
                            <div className="message-list" aria-live="polite">
                                {messages.map((message) =>
                                    message.role === 'user' ? (
                                        <article key={message.id} className="chat-message user">
                                            <div className="user-bubble">{message.text}</div>
                                            <span className="message-author">You</span>
                                        </article>
                                    ) : (
                                        <article
                                            key={message.id}
                                            className={`chat-message assistant ${message.status || ''}`}
                                        >
                                            <div className="assistant-avatar">
                                                <BrandMark />
                                            </div>
                            <div className="assistant-response">
                                                {!message.text &&
                                                !message.artifacts?.length &&
                                                message.status === 'streaming' ? (
                                                    <ThinkingIndicator
                                                        message={
                                                            message.progress ||
                                                            'Connecting to RTB data'
                                                        }
                                                    />
                                                ) : (
                                                    <>
                                                        {message.status === 'streaming' &&
                                                            message.progress && (
                                                                <span className="stream-status">
                                                                    {message.progress}
                                                                </span>
                                                            )}
                                                        <RichMessage
                                                            text={message.text}
                                                            isStreaming={
                                                                message.status === 'streaming'
                                                            }
                                                            tools={message.tools}
                                                            citations={message.citations}
                                                            artifacts={message.artifacts}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </article>
                                    ),
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                            <div className="composer-dock">
                                <Composer
                                    draft={draft}
                                    disabled={isSending}
                                    variant="dock"
                                    onChange={setDraft}
                                    onSubmit={handleSubmit}
                                    onKeyDown={handleComposerKeyDown}
                                />
                                {chatError && <p className="chat-error">{chatError}</p>}
                            </div>
                        </section>
                    </section>
                )}
                {config && !hasConversation && (
                    <section className="home-experience">
                        <div className="hero-copy">
                            <div className="hero-mark">
                                <BrandMark />
                                <span className="hero-pulse" />
                            </div>
                            <h1>
                                Close the Gap to Benchmark.{' '}
                                <span>Faster.</span>
                            </h1>
                            <p className="hero-subline">
                                Operational precision, powered by AgentCore.
                            </p>
                        </div>

                        <Composer
                            draft={draft}
                            disabled={isSending}
                            variant="hero"
                            onChange={setDraft}
                            onSubmit={handleSubmit}
                            onKeyDown={handleComposerKeyDown}
                        />

                        <div className="suggestion-section">
                            <div className="suggestion-heading">
                                <span>Try asking</span>
                                <span>Choose a prompt to get started</span>
                            </div>
                            <div className="suggestion-grid">
                                {PROMPT_SUGGESTIONS.map((suggestion) => (
                                    <button
                                        key={suggestion.prompt}
                                        className="suggestion-card"
                                        type="button"
                                        onClick={() => submitPrompt(suggestion.prompt)}
                                    >
                                        <span className={`suggestion-icon ${suggestion.icon}`}>
                                            <PromptIcon name={suggestion.icon} />
                                        </span>
                                        <span className="suggestion-text">
                                            <small>{suggestion.category}</small>
                                            <strong>{suggestion.title}</strong>
                                            <span>{suggestion.prompt}</span>
                                        </span>
                                        <span className="suggestion-arrow" aria-hidden="true">
                                            ↗
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                    </section>
                )}
        </main>
    );
}

export default App;
